import { osnovaApi } from '../base';
import type { LoginResponse, TokenResponse, TotpSetupResponse } from '../types';
import type { UserContext } from '@entities/user/types';

export interface OnlineUserItem {
  id: string;
  full_name: string;
  email: string;
  department: string | null;
  roles: string[];
}

export interface OnlineUsersResponse {
  count: number;
  users: OnlineUserItem[];
}

export const authApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    login: b.mutation<LoginResponse, { email: string; password: string }>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
    }),
    loginTotp: b.mutation<TokenResponse, { challenge: string; code: string }>({
      query: (body) => ({ url: '/auth/login/totp', method: 'POST', body }),
    }),
    /** Второй шаг входа по почте: код из письма в обмен на токены. */
    loginEmailCode: b.mutation<TokenResponse, { challenge: string; code: string }>({
      query: (body) => ({ url: '/auth/login/email-code', method: 'POST', body }),
    }),
    /** Прислать код ещё раз — тот же вход, новое письмо. */
    resendLoginCode: b.mutation<{ ok: boolean; email_hint: string }, { challenge: string }>({
      query: (body) => ({ url: '/auth/login/email-code/resend', method: 'POST', body }),
    }),
    logout: b.mutation<{ ok: boolean }, { refreshToken?: string | null }>({
      query: (body) => ({
        url: '/auth/logout',
        method: 'POST',
        // body с refresh_token: бэкенд отзывает ОБА токена (access по
        // Authorization-хедеру + refresh из тела). Без этого украденный
        // refresh пережил бы logout (TTL 7 дней) — аудит 20.07 §10.
        body,
      }),
      // При logout сбрасываем «личный» кеш — на новый логин подгрузим
      // свежий UserContext под нового юзера.
      invalidatesTags: ['Me'],
    }),
    me: b.query<UserContext, void>({
      query: () => '/auth/me',
      providesTags: ['Me'],
      // UserContext (профиль, права, состояние 2FA) почти статичен. Держим
      // кэш долго — 30 минут — чтобы AppShell, RealtimeBadge, AdminGuard
      // и другие подписчики мгновенно получали данные при навигации.
      // При фактическом изменении (логин/логаут/админ обновил роли) кеш
      // инвалидируется через 'Me' тег.
      keepUnusedDataFor: 1800,
    }),
    onlineUsers: b.query<OnlineUsersResponse, void>({
      query: () => '/auth/online',
      keepUnusedDataFor: 30,
    }),
    /** Шаг 1: сгенерировать pending TOTP secret + QR. */
    setupTotp: b.mutation<TotpSetupResponse, void>({
      query: () => ({ url: '/auth/2fa/setup', method: 'POST' }),
    }),
    /** Шаг 2: подтвердить кодом и включить 2FA. */
    enableTotp: b.mutation<{ ok: boolean }, { code: string }>({
      query: (body) => ({ url: '/auth/2fa/enable', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),
    /** Отключить 2FA (требует и текущий пароль, и код из authenticator). */
    disableTotp: b.mutation<{ ok: boolean }, { password: string; code: string }>({
      query: (body) => ({ url: '/auth/2fa/disable', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),

    /** Enforcement-флоу (третий шаг LoginPage): сгенерировать pending secret + QR
     *  по setup-токену. Юзер ещё НЕ вошёл (access-токена в Redux нет), поэтому
     *  авторизуемся коротким setup-токеном явным заголовком. baseQuery не
     *  перетрёт его: prepareHeaders ставит Authorization только при наличии
     *  accessToken, а на /login его нет (иначе LoginPage редиректнул бы на '/'). */
    setupTotpEnforced: b.mutation<TotpSetupResponse, { setupToken: string }>({
      query: ({ setupToken }) => ({
        url: '/auth/2fa/setup',
        method: 'POST',
        headers: { Authorization: `Bearer ${setupToken}` },
      }),
    }),
    /** Enforcement-флоу: подтвердить кодом → бэкенд активирует 2FA и в одном
     *  ответе отдаёт полноценный TokenPair (kind='authenticated'). */
    enableTotpEnforced: b.mutation<
      { kind: 'authenticated' } & TokenResponse,
      { setupToken: string; code: string }
    >({
      query: ({ setupToken, code }) => ({
        url: '/auth/2fa/enable',
        method: 'POST',
        headers: { Authorization: `Bearer ${setupToken}` },
        body: { code },
      }),
    }),

  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useLoginTotpMutation,
  useLoginEmailCodeMutation,
  useResendLoginCodeMutation,
  useLogoutMutation,
  useMeQuery,
  useOnlineUsersQuery,
  useSetupTotpMutation,
  useEnableTotpMutation,
  useDisableTotpMutation,
  useSetupTotpEnforcedMutation,
  useEnableTotpEnforcedMutation,
} = authApi;
